import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { FaTimes, FaExternalLinkAlt, FaCheck, FaCopy } from 'react-icons/fa';
import {
  TbCircleNumber1Filled,
  TbCircleNumber2Filled,
  TbCircleNumber3Filled,
  TbCircleNumber4Filled,
  TbCircleNumber5Filled,
  TbCircleNumber6Filled
} from "react-icons/tb";
import './PartnerModal.css';

export default function PartnerModal({ partner, attrs = [], tags = [], onClose }) {
  const closeBtnRef = useRef(null);
  const [isClosing, setIsClosing] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyLink = useCallback(() => {
    if (partner?.href) {
      navigator.clipboard.writeText(partner.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [partner?.href]);

  const startClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
  }, [isClosing]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') startClose();
    }
    document.addEventListener('keydown', onKey);
    setTimeout(() => closeBtnRef.current?.focus(), 50);
    
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${scrollbarWidth}px`;
    document.body.classList.add('partner-modal-open');
    
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.body.classList.remove('partner-modal-open');
    };
  }, [startClose]);

  if (!partner) return null;

  function handleAnimationEnd(e) {
    if (isClosing && e.target.classList && e.target.classList.contains('partner-modal')) {
      onClose?.();
    }
  }

  const benefits = partner.benefits && partner.benefits.length > 0
    ? partner.benefits
    : [];

  const stepIcons = [
    <TbCircleNumber1Filled key="s1" />,
    <TbCircleNumber2Filled key="s2" />,
    <TbCircleNumber3Filled key="s3" />,
    <TbCircleNumber4Filled key="s4" />,
    <TbCircleNumber5Filled key="s5" />,
    <TbCircleNumber6Filled key="s6" />
  ];

  function renderTextWithSteps(text) {
    const lines = String(text || '').split('\n');
    return lines.map((rawLine, idx) => {
      const line = rawLine.trim();
      const match = line.match(/^\s*(\d+)[\uFE0F\u20E3).\\-–—\s]*(.+)$/);
      if (match) {
        const n = parseInt(match[1], 10);
        const content = match[2].trim();
        const icon = stepIcons[n - 1] || stepIcons[0];
        return (
          <p key={idx} className="partner-step-line">
            <span className="partner-step-icon" aria-hidden="true">{icon}</span>
            <span className="partner-step-text" dangerouslySetInnerHTML={{ __html: content }} />
          </p>
        );
      }
      if (line === '') return <div key={idx} style={{ height: 6 }} />;
      return (
        <p key={idx} className="partner-modal-text-line" dangerouslySetInnerHTML={{ __html: line }} />
      );
    });
  }

  const node = (
    <div
      className={`partner-modal-overlay ${isClosing ? 'closing' : 'opening'}`}
      onPointerDown={startClose}
      onClick={startClose}
      role="dialog"
      aria-modal="true"
    >
      <aside
        className={`partner-modal ${isClosing ? 'closing' : 'opening'}`}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        onAnimationEnd={handleAnimationEnd}
      >
        <header className="partner-modal-header">
          <button ref={closeBtnRef} className="partner-modal-close" onClick={startClose} aria-label="Close">
            <FaTimes />
          </button>
        </header>

        <div className="partner-modal-body">
          <div className="partner-modal-top-row">
            <img 
              src={partner.logo} 
              alt={partner.name} 
              className="partner-modal-logo"
              style={{ backgroundColor: partner.background_color || '#1a1a2e' }}
            />
            <div className="partner-modal-textblock">
              <h4 className="partner-modal-company">{partner.name}</h4>
              <p className="partner-modal-description">{partner.description}</p>
            </div>
          </div>

          <div className="partner-modal-tags">
            {tags.map((t, i) => (
              <span key={i} className={`partner-modal-tag tag-${t.variant || 'default'}`}>{t.label}</span>
            ))}
          </div>

          <div className="partner-modal-text">
            {renderTextWithSteps(partner.text)}
          </div>

          {benefits.length > 0 && (
            <section className="partner-modal-benefits">
              <h5 className="benefits-title">Benefícios</h5>
              <ul className="benefits-list">
                {benefits.map((b, i) => (
                  <li key={i} className="benefit-item">
                    <FaCheck className="benefit-icon" />
                    <span
                      className="benefit-text"
                      dangerouslySetInnerHTML={{ __html: b }}
                    />
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        <footer className="partner-modal-footer">
          <div className="partner-link-container">
            <div className="partner-link-box">
              <span className="partner-link-url">{partner.href}</span>
              <button type="button" className="partner-copy-btn" onClick={copyLink} aria-label="Copiar link">
                {copied ? <FaCheck /> : <FaCopy />}
              </button>
            </div>
            <a className="partner-modal-cta" href={partner.href} target="_blank" rel="noreferrer" onClick={startClose}>
              Visitar <FaExternalLinkAlt className="cta-icon" />
            </a>
          </div>
        </footer>
      </aside>
    </div>
  );

  return ReactDOM.createPortal(node, document.body);
}
